"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.findNextNonce = exports.getAccountShape = exports.mapTxToOps = exports.getAddress = exports.getUnit = exports.getTxToBroadcast = void 0;
const bignumber_js_1 = require("bignumber.js");
const bn_js_1 = __importDefault(require("bn.js"));
const flatMap_1 = __importDefault(require("lodash/flatMap"));
const transactions_1 = require("@stacks/transactions");
const account_1 = require("../../../../account");
const api_1 = require("../../bridge/utils/api");
const api_types_1 = require("./api.types");
const currencies_1 = require("../../../../currencies");
const operation_1 = require("../../../../operation");
const getTxToBroadcast = (operation, signature, signatureRaw) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const { value, recipients, fee, extra: { memo }, } = operation;
    const { anchorMode, network, xpub } = signatureRaw;
    const options = {
        amount: new bn_js_1.default((0, bignumber_js_1.BigNumber)(value).minus(fee).toFixed()),
        recipient: recipients[0],
        anchorMode,
        memo,
        network: api_types_1.StacksNetwork[network],
        publicKey: xpub,
        fee: new bn_js_1.default((0, bignumber_js_1.BigNumber)(fee).toFixed()),
        nonce: new bn_js_1.default((_a = operation.transactionSequenceNumber) !== null && _a !== void 0 ? _a : 0),
    };
    const tx = yield (0, transactions_1.makeUnsignedSTXTokenTransfer)(options);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore need to ignore the TS error here
    tx.auth.spendingCondition.signature = (0, transactions_1.createMessageSignature)(signature);
    return tx.serialize();
});
exports.getTxToBroadcast = getTxToBroadcast;
const getUnit = () => (0, currencies_1.getCryptoCurrencyById)("stacks").units[0];
exports.getUnit = getUnit;
const getAddress = (a) => a.freshAddresses.length > 0
    ? a.freshAddresses[0]
    : { address: a.freshAddress, derivationPath: a.freshAddressPath };
exports.getAddress = getAddress;
const mapTxToOps = (accountID, { address }) => (tx) => {
    const { sender, recipient, amount } = tx.stx_transfers[0];
    const { tx_id, fee_rate, nonce, block_height, burn_block_time, token_transfer } = tx.tx;
    const { memo: memoHex } = token_transfer;
    const ops = [];
    const date = new Date(burn_block_time * 1000);
    const value = new bignumber_js_1.BigNumber(amount || "0");
    const feeToUse = new bignumber_js_1.BigNumber(fee_rate || "0");
    const isSending = address === sender;
    const isReceiving = address === recipient;
    const memo = Buffer.from(memoHex.substring(2), "hex").toString().replaceAll("\x00", "");
    if (isSending) {
        ops.push({
            id: (0, operation_1.encodeOperationId)(accountID, tx_id, "OUT"),
            hash: tx_id,
            type: "OUT",
            value: value.plus(feeToUse),
            fee: feeToUse,
            blockHeight: block_height,
            blockHash: null,
            accountId: accountID,
            senders: [sender],
            recipients: [recipient],
            transactionSequenceNumber: nonce,
            date,
            extra: {
                memo,
            },
        });
    }
    if (isReceiving) {
        ops.push({
            id: (0, operation_1.encodeOperationId)(accountID, tx_id, "IN"),
            hash: tx_id,
            type: "IN",
            value,
            fee: feeToUse,
            blockHeight: block_height,
            blockHash: null,
            accountId: accountID,
            senders: [sender],
            recipients: [recipient],
            transactionSequenceNumber: nonce,
            date,
            extra: {
                memo,
            },
        });
    }
    return ops;
};
exports.mapTxToOps = mapTxToOps;
const getAccountShape = (info) => __awaiter(void 0, void 0, void 0, function* () {
    const { initialAccount, address, currency, rest = {}, derivationMode } = info;
    const publicKey = reconciliatePublicKey(rest.publicKey, initialAccount);
    const accountId = (0, account_1.encodeAccountId)({
        type: "js",
        version: "2",
        currencyId: currency.id,
        xpubOrAddress: publicKey,
        derivationMode,
    });
    const blockHeight = yield (0, api_1.fetchBlockHeight)();
    const balanceResp = yield (0, api_1.fetchBalances)(address);
    const rawTxs = yield (0, api_1.fetchFullTxs)(address);
    const mempoolTxs = yield (0, api_1.fetchFullMempoolTxs)(address);
    const balance = new bignumber_js_1.BigNumber(balanceResp.balance);
    let spendableBalance = new bignumber_js_1.BigNumber(balanceResp.balance);
    for (const tx of mempoolTxs) {
        spendableBalance = spendableBalance
            .minus(new bignumber_js_1.BigNumber(tx.fee_rate))
            .minus(new bignumber_js_1.BigNumber(tx.token_transfer.amount));
    }
    const result = {
        id: accountId,
        xpub: publicKey,
        freshAddress: address,
        balance,
        spendableBalance,
        operations: (0, flatMap_1.default)(rawTxs, (0, exports.mapTxToOps)(accountId, info)),
        blockHeight: blockHeight.chain_tip.block_height,
    };
    return result;
});
exports.getAccountShape = getAccountShape;
function reconciliatePublicKey(publicKey, initialAccount) {
    if (publicKey)
        return publicKey;
    if (initialAccount) {
        const { xpubOrAddress } = (0, account_1.decodeAccountId)(initialAccount.id);
        return xpubOrAddress;
    }
    throw new Error("publicKey wasn't properly restored");
}
const findNextNonce = (senderAddress, pendingOps) => __awaiter(void 0, void 0, void 0, function* () {
    let nextNonce = (0, bignumber_js_1.BigNumber)(0);
    for (const op of pendingOps) {
        const nonce = op.transactionSequenceNumber
            ? new bignumber_js_1.BigNumber(op.transactionSequenceNumber)
            : new bignumber_js_1.BigNumber(0);
        if (nonce.gt(nextNonce)) {
            nextNonce = nonce;
        }
    }
    const allMempoolTxns = yield (0, api_1.fetchFullMempoolTxs)(senderAddress);
    for (const tx of allMempoolTxns) {
        const nonce = (0, bignumber_js_1.BigNumber)(tx.nonce);
        if (nonce.gt(nextNonce)) {
            nextNonce = nonce;
        }
    }
    if (!nextNonce.eq(0)) {
        nextNonce = nextNonce.plus(1);
    }
    const nonceResp = yield (0, api_1.fetchNonce)(senderAddress);
    const possibleNextNonce = new bignumber_js_1.BigNumber(nonceResp.possible_next_nonce);
    if (possibleNextNonce.gt(nextNonce)) {
        nextNonce = possibleNextNonce;
    }
    return nextNonce;
});
exports.findNextNonce = findNextNonce;
//# sourceMappingURL=misc.js.map